import { Info } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"
import type {
  MissionDashboardStateFilter,
  MissionFunnelStage,
  MissionFunnelStageKey,
  MissionType,
} from "@/types/missions"

/**
 * OKS-31 — Per-mission funnel card on the dashboard.
 *
 * Renders one horizontal `Progress` bar per stage. Promotional ships the
 * full 8-stage pipeline (assigned → accepted → purchased → to_ship →
 * shipped → delivered → posted → completed); Open/Drop ship the 4-stage
 * subset (assigned / accepted / posted / completed). Two muted trailing
 * chips show `issue` and `declined` counts (terminal-non-success — never
 * count toward Completed).
 *
 * Desktop bars are clickable buttons — clicking mutates `?state=<stage>`
 * via the dashboard's URL hook, narrowing the per-creator table below to
 * the matching state. Aria-label spells out the action for screen readers.
 *
 * OKS-212 — Mobile fold (UX Spec / OKS-33 KD-6). On phones the three
 * contiguous shipping sub-stages (purchased + to_ship + shipped) collapse
 * into a single "Shipping" row (count = their sum) to cut scroll-fatigue,
 * giving 6 bars on phone vs 8 on desktop. Implemented as a CSS-only
 * Tailwind `md:` dual-render — both lists are in the DOM, only one is in
 * layout + the a11y tree at a time (`hidden md:flex` desktop / `flex
 * md:hidden` mobile) — mirroring the sibling `brand-mission-collabs-panel`.
 * No `useIsMobile`/matchMedia hook (avoids an SSR→client 8→6 hydration
 * flash). The fold keys off the *presence* of the purchased/to_ship/shipped
 * trio in `funnel` (not `missionType`), so 4-stage funnels render mobile ==
 * desktop with no fold, and a future Drop pipeline carrying the trio folds
 * for free. The folded Shipping row is display-only (not a button): no
 * single `?state=` maps to the 3-state union, so it advertises no action it
 * can't perform; the 5 non-folded mobile bars keep click-to-filter.
 *
 * Zero-count stage edge: bar renders at ~2% width with a `0` count label
 * (NOT collapsed to zero width — stage visibility preserved per UX Spec §3).
 * The folded Shipping row follows the same posture: trio all 0 → renders
 * with a `0` count, never hidden.
 */

/** The three contiguous Promotional sub-stages folded into one mobile row. */
const SHIPPING_FOLD_STAGES: readonly MissionFunnelStageKey[] = [
  "purchased",
  "to_ship",
  "shipped",
]

/**
 * Component-internal mobile-list row. Either a pass-through backend stage
 * (interactive, click-to-filter) or the synthetic folded "shipping"
 * aggregate (display-only). `shipping` is deliberately NOT a
 * `MissionFunnelStageKey` — it is not a real backend stage.
 */
type MobileFunnelRow =
  | {
      kind: "stage"
      stage: MissionFunnelStageKey
      count: number
      labelKey: string
    }
  | { kind: "shipping"; count: number }

interface MissionDashboardFunnelProps {
  funnel: MissionFunnelStage[]
  declinedCount: number
  issueCount: number
  missionType: MissionType
  /** Currently-active state filter — used to highlight the matching bar. */
  activeState: MissionDashboardStateFilter
  /** Click handler — funnel-bar click mutates `?state=<stage>`. */
  onStageSelect: (stage: MissionDashboardStateFilter) => void
}

/**
 * Build the mobile (≤6) row list from the backend `funnel`. The first
 * shipping sub-stage encountered is replaced by one folded `shipping` row
 * (count = sum of every present sub-stage); the other sub-stages are
 * dropped. Stage order is preserved (the trio is contiguous). When the
 * funnel carries no shipping sub-stage (Open/Drop 4-stage), the result is
 * a 1:1 pass-through == the desktop list (no fold).
 */
function buildMobileRows(funnel: MissionFunnelStage[]): MobileFunnelRow[] {
  const shippingTotal = funnel
    .filter((s) => SHIPPING_FOLD_STAGES.includes(s.stage))
    .reduce((sum, s) => sum + s.count, 0)

  const rows: MobileFunnelRow[] = []
  let foldInserted = false

  for (const stage of funnel) {
    if (SHIPPING_FOLD_STAGES.includes(stage.stage)) {
      if (!foldInserted) {
        rows.push({ kind: "shipping", count: shippingTotal })
        foldInserted = true
      }
      // Subsequent sub-stages are folded into the single row above.
      continue
    }
    rows.push({
      kind: "stage",
      stage: stage.stage,
      count: stage.count,
      labelKey: stage.label_key,
    })
  }

  return rows
}

interface FunnelBarProps {
  label: string
  count: number
  /** Largest count in the bar's OWN list, so fills are proportional in-view. */
  max: number
  ariaLabel: string
  barTestId: string
  countTestId: string
  dataState: string
  /** Interactive bars are `<button>` (click-to-filter); others are static. */
  interactive: boolean
  isActive?: boolean
  onSelect?: () => void
}

/**
 * One funnel row: fixed-width label, proportional `Progress` fill, and a
 * right-aligned monospace count. Renders as a `<button>` when `interactive`
 * (existing hover/focus/aria-pressed posture) or a static `<div>` for the
 * display-only folded Shipping row.
 */
function FunnelBar({
  label,
  count,
  max,
  ariaLabel,
  barTestId,
  countTestId,
  dataState,
  interactive,
  isActive = false,
  onSelect,
}: FunnelBarProps) {
  const proportional = Math.max(2, (count / max) * 100)

  const body = (
    <>
      <span className="text-foreground w-32 shrink-0 text-sm font-medium">
        {label}
      </span>
      <Progress
        value={proportional}
        className="h-3 flex-1"
        aria-hidden="true"
      />
      <span
        className="text-foreground w-10 shrink-0 text-right font-mono text-sm tabular-nums"
        data-testid={countTestId}
      >
        {count}
      </span>
    </>
  )

  if (!interactive) {
    return (
      <div
        aria-label={ariaLabel}
        className="flex w-full items-center gap-3 rounded-md p-2 text-left"
        data-testid={barTestId}
        data-state={dataState}
      >
        {body}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={ariaLabel}
      aria-pressed={isActive}
      className={cn(
        "group flex w-full items-center gap-3 rounded-md p-2 text-left",
        "hover:bg-muted/40 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
        isActive && "bg-muted/40",
      )}
      data-testid={barTestId}
      data-state={dataState}
      data-active={isActive ? "true" : "false"}
    >
      {body}
    </button>
  )
}

export function MissionDashboardFunnel({
  funnel,
  declinedCount,
  issueCount,
  missionType,
  activeState,
  onStageSelect,
}: MissionDashboardFunnelProps) {
  const { t } = useTranslation()

  const desktopMax = Math.max(1, ...funnel.map((s) => s.count))
  const mobileRows = useMemo(() => buildMobileRows(funnel), [funnel])
  const mobileMax = Math.max(1, ...mobileRows.map((r) => r.count))

  // OKS-33 — Promotional now ships, so its banner is suppressed. Drop
  // (OKS-32) ALSO ships now, but we keep the banner as a no-op safety
  // net until the legacy fixture stops emitting it; UX Spec §2 puts the
  // banner-removal lock on this ticket only.
  const showBanner = missionType === "drop"

  return (
    <Card data-testid="mission-dashboard-funnel">
      <CardHeader>
        <CardTitle>{t("missions.dashboard.funnel.title")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {showBanner && (
          <Alert data-testid="mission-dashboard-funnel-banner">
            <Info className="size-4" aria-hidden="true" />
            <AlertDescription>
              {missionType === "drop"
                ? t("missions.dashboard.funnel.banner_drop")
                : t("missions.dashboard.funnel.banner_promotional")}
            </AlertDescription>
          </Alert>
        )}

        {/* Desktop / tablet — full per-stage list (≥md). Unchanged behavior. */}
        <ul
          className="hidden flex-col gap-2 md:flex"
          data-testid="mission-dashboard-funnel-list-desktop"
        >
          {funnel.map((stage) => {
            const isActive = activeState === stage.stage
            return (
              <li key={stage.stage} className="flex items-center gap-3">
                <FunnelBar
                  label={t(stage.label_key)}
                  count={stage.count}
                  max={desktopMax}
                  ariaLabel={t("missions.dashboard.funnel.bar_aria", {
                    count: stage.count,
                    state: t(stage.label_key),
                  })}
                  barTestId={`mission-dashboard-funnel-bar-${stage.stage}`}
                  countTestId={`mission-dashboard-funnel-count-${stage.stage}`}
                  dataState={stage.stage}
                  interactive
                  isActive={isActive}
                  onSelect={() => onStageSelect(stage.stage)}
                />
              </li>
            )
          })}
        </ul>

        {/* Mobile — shipping trio folded into one display-only row (<md). */}
        <ul
          className="flex flex-col gap-2 md:hidden"
          data-testid="mission-dashboard-funnel-list-mobile"
        >
          {mobileRows.map((row) => {
            if (row.kind === "shipping") {
              return (
                <li key="shipping" className="flex items-center gap-3">
                  <FunnelBar
                    label={t("missions.dashboard.funnel.stages.shipping")}
                    count={row.count}
                    max={mobileMax}
                    ariaLabel={t("missions.dashboard.funnel.shipping_aria", {
                      count: row.count,
                    })}
                    barTestId="mission-dashboard-funnel-mobile-bar-shipping"
                    countTestId="mission-dashboard-funnel-mobile-count-shipping"
                    dataState="shipping"
                    interactive={false}
                  />
                </li>
              )
            }

            const isActive = activeState === row.stage
            return (
              <li key={row.stage} className="flex items-center gap-3">
                <FunnelBar
                  label={t(row.labelKey)}
                  count={row.count}
                  max={mobileMax}
                  ariaLabel={t("missions.dashboard.funnel.bar_aria", {
                    count: row.count,
                    state: t(row.labelKey),
                  })}
                  barTestId={`mission-dashboard-funnel-mobile-bar-${row.stage}`}
                  countTestId={`mission-dashboard-funnel-mobile-count-${row.stage}`}
                  dataState={row.stage}
                  interactive
                  isActive={isActive}
                  onSelect={() => onStageSelect(row.stage)}
                />
              </li>
            )
          })}
        </ul>

        <div
          className="flex flex-wrap items-center gap-2 pt-1"
          data-testid="mission-dashboard-funnel-trailing"
          aria-label={t("missions.dashboard.funnel.trailing_aria")}
        >
          <Badge
            variant="outline"
            className="text-muted-foreground"
            data-testid="mission-dashboard-funnel-issue-chip"
          >
            {t("missions.dashboard.funnel.issue_chip", { count: issueCount })}
          </Badge>
          <Badge
            variant="outline"
            className="text-muted-foreground"
            data-testid="mission-dashboard-funnel-declined-chip"
          >
            {t("missions.dashboard.funnel.declined_chip", {
              count: declinedCount,
            })}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}
